import { rem } from 'polished'
import React, { MouseEvent, PropsWithChildren, useImperativeHandle, useRef } from 'react'
import styled from 'styled-components'
import CloseButton from '../../Common/CloseButton'
import Heading from '../Typography/Heading'
import cn from 'clsx'
import BodyText from '../Typography/BodyText'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import variables from 'styles/vendor/bootstrap/variables'
import Pane from 'components/Common/Pane'
import useScrollEndsObserver from 'hooks/useScrollEndsObserver'

const DropdownFooter = styled(Pane)`
  padding: ${rem(20)};
  position: sticky;
  bottom: 0;
  transition: box-shadow 0.2s;

  &.has-shadow {
    box-shadow: ${props => props.theme.shadow.top.small};
  }
`

const Header = styled(Group)`
  padding: ${rem(16)};
  padding-bottom: ${rem(12)};
  position: sticky;
  top: 0;
  z-index: 1;
  background-color: ${props => props.theme.palette.neutral.eight};
  transition: box-shadow 0.2s;

  &.has-shadow  {
    box-shadow: ${props => props.theme.shadow.bottom.small};
  }
`

const Container = styled.div`
  /* max width of whole screen minus the common shift/padding of 16 x2 (both sides)  */
  max-width: min(${variables.containerLarge}, calc(100vw - ${rem(32)}));
  box-shadow: ${props => props.theme.shadow.bottom.small};
  border: 1px solid ${props => props.theme.palette.neutral.six};
  border-radius: ${props => props.theme.borderRadius.S};
  background-color: ${props => props.theme.palette.neutral.eight};
  /* 32px gutter */
  min-width: min(${rem(400)},  calc(100vw - 32px));
  overflow: auto;
  -webkit-overflow-scrolling: touch;

  &.fit-dropdown-items {
    min-width: unset;
    max-height: min(calc(var(--vh, 1vh) * 100 - ${rem(200)}), ${rem(820)});

    > * {
      white-space: nowrap;
    }
  }

  &.fit-auto {
    min-width: unset;
  }

  &.inheritMaxHeight {
    max-height: inherit;
  }
`

const Content = styled.div`
  padding: ${rem(20)};
  width: 100%;

  &.fit-container {
    padding: 0;
  }

  &.fit-dropdown-items {
    padding: ${rem(8)};
  }


`

interface BaseProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
  // Use dropdown-items when your content are dropdown items. They have their own padding so we need
  // to specifically cater for this components to work with it
  fit?: 'auto' | 'dropdown-items' | 'container';
  style?: React.CSSProperties;
  'data-testid'?: string;
  footer?: React.ReactNode;
  /** Use in conjunction with autoHeight on the Dropdown V2 component */
  inheritMaxHeight?: boolean;
}

interface PropsWithTitle extends BaseProps {
  title: string
  subtitle?: string
  onClose?: (e: MouseEvent<HTMLButtonElement>) => void;

}

interface PropsWithNoTitle extends BaseProps {
  title?: undefined | ''
  subtitle?: undefined
  onClose?: undefined
}

type Props = PropsWithTitle | PropsWithNoTitle

const DropdownContainer = React.forwardRef<HTMLDivElement, PropsWithChildren<Props>>((props, ref) => {
  const { children, className, fit, title, subtitle, onClose, inheritMaxHeight, 'data-testid': testId, footer, ...rest } = props
  const bodyRef = useRef<HTMLDivElement>(null)

  useImperativeHandle(ref, () => bodyRef.current!)

  const {
    hasReachedVerticalEnd,
    hasReachedVerticalStart,
  } = useScrollEndsObserver(bodyRef, children)

  return (<Container
    {...rest}
    className={cn(className, `fit-${fit}`, { inheritMaxHeight })}
    aria-label={title}
    ref={bodyRef}
    data-testid={testId}
  >
    {!!title && <Header
      gap={12}
      direction="horizontal"
      horizontalAlign="space-between"
      className={cn({ 'has-shadow': !hasReachedVerticalStart })}
    >
      <VerticalSpacer gap={8}>
        <Heading variant="heading6">{title}</Heading>
        {!!subtitle && <BodyText variant="medium" colour="neutral-two">{subtitle}</BodyText>}
      </VerticalSpacer>
      {!!onClose && <CloseButton onClick={onClose} outdent="right"/>}
    </Header>}
    <Content className={cn(`fit-${fit}`)}>
      {children}
    </Content>
    {footer && <DropdownFooter className={cn({ 'has-shadow': !hasReachedVerticalEnd })}>
      {footer}
    </DropdownFooter>}
  </Container>

  )
})

DropdownContainer.displayName = 'DropdownContainer'

export default DropdownContainer
